<template>
    <div id="tradeTrackerEditPortfolioForm">

        <div class="relative border-t-2 border-blue-600 mt-4">
            <div v-show="dataLoading" class="absolute z-50 w-full h-full bg-spinner-ghost flex flex-col items-center">
                <spinner class="mb-1"></spinner>
            </div>

            <div class="table-header border-b">
                <div class="flex flex-wrap w-full">
                    <div class="w-full lg:w-1/2 mb-2 ">
                        <label class="inline-block self-center mr-2 mb-2 xs:w-full flex-grow">
                            Edit Title:
                            <div class="inline-block relative">
                                <div class="tooltip-error" v-show="portfolioError.short">{{ portfolioError.short }}</div>
                                <input @input="portfolioInput" :class="{'is-invalid': portfolioError.short}" class="form-input-inline-w-none form-input-sm-inline inline-block" type="text" v-model="portfolio.caption" maxlength="40" placeholder="Enter a title..." aria-label="Edit a title" />
                            </div>
                        </label>
                        <label for="cashValue" class="inline-block self-center xs:w-full">
                            Starting Cash Value:
                            <div class="inline-block xs:w-full">
                                <input id="cashValue" class="form-input-inline-w-none form-input-sm-inline w-full" type="text" v-model="portfolio.cash" placeholder="1000.00 or 1,000.00" aria-label="Cash" />
                            </div>
                        </label>
                    </div>
                    <div class="w-full lg:w-1/2 flex lg:justify-end mb-2">
                        <label for="defaultView" class="">
                            <div class="inline-block mr-1">Default View:</div>
                            <div class="inline-block">
                                <select class="form-control form-control-sm form-control-no-shadow bg-white" v-model="portfolio.default_view" aria-label="Default View">
                                    <option v-for="option in views" :value="option.value">
                                        {{ option.label }}
                                    </option>
                                </select>
                            </div>
                        </label>
                    </div>
                    <div class="w-full lg:w-3/4">
                        <label for="summaryExclude" class="inline-block self-center mb-2 lg:mb-0 mr-4 xs:w-full">
                            <div class="inline-block mr-px">
                                <input id="summaryExclude" type="checkbox" name="summary_exclude" :true-value="'1'" :false-value="'0'" v-model="portfolio.summary_exclude" aria-label="Summary Exclude">
                            </div>
                            Exclude from the All Portfolios (Summary) View
                        </label>
                        <label for="adjustCash" class="inline-block self-center mb-2 lg:mb-0 mr-4 xs:w-full">
                            <div class="inline-block mr-px">
                                <input id="adjustCash" type="checkbox" name="adjust_cash" :true-value="'1'" :false-value="'0'" v-model="portfolio.adjust_cash" aria-label="Adjust Cash">
                            </div>
                            Cash Adjustment
                        </label>
                    </div>
                    <div class="w-full lg:w-1/4 mt-2 md:mt-0 mb-1">
                        <div class="text-center lg:text-right">
                            <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1" @click="close">Cancel</div>
                            <div class="btn btn-sm btn-yellow inline-flex edit-portfolio-form-save" @click="save">Save</div>
                        </div>
                    </div>

                </div>
            </div>

            <div class="table-responsive" v-if="portfolio.items.length">
                <table class="table table-striped table-hover border">
                    <thead>
                        <tr>
                            <th class="sm:hidden w-24 text-center">Delete</th>
                            <th class="w-16 text-center">Sort</th>
                            <th @click="sortTable('contractFullName')">Name<i v-if="sort.field === 'contractFullName'" class="fas ml-1" :class="{'fa-sort-alpha-down': sort.direction === 'asc', 'fa-sort-alpha-up': sort.direction === 'desc'}" aria-hidden="true"></i></th>
                            <th class="text-center" @click="sortTable('quantity')">Qty<i v-if="sort.field === 'quantity'" class="fas ml-1" :class="{'fa-sort-alpha-down': sort.direction === 'asc', 'fa-sort-alpha-up': sort.direction === 'desc'}" aria-hidden="true"></i></th>
                            <th class="text-center" @click="sortTable('action')">Position<i v-if="sort.field === 'action'" class="fas ml-1" :class="{'fa-sort-alpha-down': sort.direction === 'asc', 'fa-sort-alpha-up': sort.direction === 'desc'}" aria-hidden="true"></i></th>
                            <th class="text-center" @click="sortTable('entry_date')">Entry Date<i v-if="sort.field === 'entry_date'" class="fas ml-1" :class="{'fa-sort-alpha-down': sort.direction === 'asc', 'fa-sort-alpha-up': sort.direction === 'desc'}" aria-hidden="true"></i></th>
                            <th class="text-center" @click="sortTable('entry_price')">Entry Price<i v-if="sort.field === 'entry_price'" class="fas ml-1" :class="{'fa-sort-alpha-down': sort.direction === 'asc', 'fa-sort-alpha-up': sort.direction === 'desc'}" aria-hidden="true"></i></th>
                            <th class="text-center" @click="sortTable('lastPrice')">Current<i v-if="sort.field === 'lastPrice'" class="fas ml-1" :class="{'fa-sort-alpha-down': sort.direction === 'asc', 'fa-sort-alpha-up': sort.direction === 'desc'}" aria-hidden="true"></i></th>
                            <th class="text-center" @click="sortTable('return')">Profit/Loss<i v-if="sort.field === 'return'" class="fas ml-1" :class="{'fa-sort-alpha-down': sort.direction === 'asc', 'fa-sort-alpha-up': sort.direction === 'desc'}" aria-hidden="true"></i></th>
                            <th class="text-center" @click="sortTable('tradeTime')">As Of<i v-if="sort.field === 'tradeTime'" class="fas ml-1" :class="{'fa-sort-alpha-down': sort.direction === 'asc', 'fa-sort-alpha-up': sort.direction === 'desc'}" aria-hidden="true"></i></th>
                            <th class="xs:hidden w-24 text-center">Delete</th>
                        </tr>
                    </thead>
                    <draggable v-model="portfolio.items" tag="tbody" handle=".canDrag" item-key="id">
                        <template #item="{ element, index }">
                        <tr>
                            <td class="sm:hidden text-center"><i @click="removePortfolioItem(index)" class="fa fa-times text-red-500 cursor-pointer" aria-hidden="true"></i></td>
                            <td class="text-center cursor-move canDrag"><i class="fas fa-arrows-alt text-gray-500"></i></td>
                            <td>{{ element.contractFullName || element.symbolName }}</td>
                            <td class="text-center">{{ element.quantity }}</td>
                            <td class="text-center">{{ element.action }}</td>
                            <td class="text-center">{{ element.entry_date }}</td>
                            <td class="text-center">{{ element.entry_price }}</td>
                            <td class="text-center" :class="{'text-blue-400': element.tradeTime=='Liquidated'}">{{ element.lastPrice }}</td>
                            <td class="text-center" :class="getClass(element.raw.return)">{{ element.return }}</td>
                            <td class="text-center" :class="{'text-blue-400': element.tradeTime=='Liquidated'}">{{ element.tradeTime }}</td>
                            <td class="xs:hidden text-center">
                                <a @click="removePortfolioItem(index)" v-tooltip="{ content: 'Delete Current Entry', placement: 'auto-start', theme: 'red' }"  class="btn btn-default btn-shadow-none btn-red-ghost btn-xs ml-1 mb-1 md:mb-0" title="Delete Entry"><i class="fas fa-times"></i></a>
                            </td>
                        </tr>
                        </template>
                    </draggable>
                    <tfoot>
                        <tr>
                            <th class="sm:hidden w-24 text-center">Delete</th>
                            <th class="text-center">Sort</th>
                            <th>Name</th>
                            <th class="text-center">Qty</th>
                            <th class="text-center">Position</th>
                            <th class="text-center">Entry Date</th>
                            <th class="text-center">Entry Price</th>
                            <th class="text-center">Current</th>
                            <th class="text-center">Profit/Loss</th>
                            <th class="text-center">As Of</th>
                            <th class="xs:hidden w-24 text-center">Delete</th>
                        </tr>
                    </tfoot>
                </table>
            </div>
            <alert-box class="mb-6" v-else>
                <template #body>
                    No transactions have been added. Please create one by going to the blue dropdown menu "Add a Transaction".
                </template>
            </alert-box>
        </div>

        <div class="flex justify-center mt-4">
            <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1 xs:ml-0" @click="close">Cancel</div>
            <div class="btn btn-sm btn-yellow inline-flex mx-1 edit-portfolio-form-save" @click="save">Save</div>
        </div>
        <div class="flex justify-center mt-4" v-if="portfolioError.long">
            <span class="error">{{ portfolioError.long }}</span>
        </div>

    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import Draggable from 'vuedraggable';

const props = defineProps({
    initialPortfolio: Object,
});

const emit = defineEmits(['close', 'save']);

const views = [
    {
        label: 'Portfolio',
        value: 'portfolio',
        default: false
    },
    {
        label: 'Quote Board',
        value: 'main',
        default: false
    }
];

const dataLoading = ref(false);
const portfolio = ref(JSON.parse(JSON.stringify(props.initialPortfolio)));
const portfolioError = ref({
    short: '',
    long: '',
});
const sort = ref({
    field: '',
    direction: 'asc',
});

const hasChanged = () => {
    return (JSON.stringify(props.initialPortfolio.items) !== JSON.stringify(portfolio.value.items));
};

const sortTable = (field) => {
    if (sort.value.field === field) {
        sort.value.direction = sort.value.direction === 'asc' ? 'desc' : 'asc';
    }
    sort.value.field = field;
    portfolio.value.items.sort((a, b) => {
        const aField = a.raw ? a.raw[field] : a[field];
        const bField = b.raw ? b.raw[field] : b[field];
        if (aField < bField) {
            return sort.value.direction === 'asc' ? -1 : 1;
        }
        if (aField > bField) {
            return sort.value.direction === 'asc' ? 1 : -1;
        }
        return 0;
    });
};

const getClass = (value) => {
    if (typeof (value) === 'string') {
        value = value.replace(/\$|,/g, '');
    }
    if (value > 0) {
        return 'text-up';
    } else if (value < 0) {
        return 'text-down';
    } else if (value == 0) {
        return 'text-unch';
    }
};

const close = () => {
    emit('close');
};

const portfolioInput = () => {
    portfolioError.value.short = '';
    portfolioError.value.long = '';
};

const validate = () => {
    let valid = true;
    if (portfolio.value.caption == '') {
        portfolioError.value.short = 'Please enter a title...';
        portfolioError.value.long = 'The portfolio title cannot be blank. Please enter a title.';
        return false;
    }
    return valid;
};

const save = () => {
    dataLoading.value = true;
    if (!validate()) {
        dataLoading.value = false;
        return;
    }

    const initialPortfolioItemIds = props.initialPortfolio.items.filter(c => c.id != undefined);
    const portfolioItemIds = portfolio.value.items.filter(c => c.id != undefined);
    const removePortfolioItems =
        [initialPortfolioItemIds, portfolioItemIds]
            .sort(
                (a, b) => b.length - a.length
            )
            .reduce(
                (a, b) => a.filter(
                    o => !b.some(
                        v => v.id === o.id
                    )
                )
            )
            .map(x => x.id);

    const route = window.route('api.trade-tracker.update-item-batch');
    axios.post(route, {
        portfolio: portfolio.value,
        portfolioItems: portfolio.value.items,
        removePortfolioItems: removePortfolioItems,
    })
        .then((response) => {
            emit('save', response.data.data);
            dataLoading.value = false;
        }).catch((error) => {
            if (error && error.response.data.message) {
                portfolioError.value.long = error.response.data.message;
            } else {
                portfolioError.value.long = 'Error updating portfolio. Something went wrong, please try again later';
            }
            console.error(error);
            dataLoading.value = false;
        })
};

const removePortfolioItem = (index) => {
    portfolio.value.items.splice(index, 1);
};

const handleLeave = (e) => {
    if (hasChanged()) {
        e.preventDefault();
        e.returnValue = 'Are you sure?';
        return 'Are you sure?';
    }
};

onMounted(() => {
    window.addEventListener('beforeunload', handleLeave);
    if (typeof (portfolio.value.cash) === 'string') {
        portfolio.value.cash = portfolio.value.cash.replace(/\$|,/g, '');
    }
});

onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleLeave);
});
</script>

<style lang="scss">
#tradeTrackerEditPortfolioForm {
    .tooltip-error {
        @apply absolute z-50 bg-red-500 text-white text-xs font-normal px-2 py-1 -mt-7 border-red-500 border-b rounded;
    }
    table {
        thead,
        tfoot {
            td, th {
                @apply px-3 py-2;
            }
        }
        tbody {
            td, th {
                @apply px-3;
            }
        }
        td, th {
            @apply px-3;
        }
        .sortable {
            @apply cursor-pointer;
        }
    }
}
</style>
